import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from '@chakra-ui/react';
import { MapPin, Warning } from 'phosphor-react';
import { JUNTA_POR_UF, resolverRegistry, UFS_ATENDIDAS, ufExigeMunicipio } from '../registries';

export interface LocalDoProcesso {
  junta_uf: string;
  junta_sigla: string;
  municipio: string | null;
}

interface TrocarLocalModalProps {
  isOpen: boolean;
  onClose: () => void;
  ufAtual?: string | null;
  municipioAtual?: string | null;
  isLoading?: boolean;
  // eslint-disable-next-line no-unused-vars
  onConfirm: (local: LocalDoProcesso) => void;
}

/**
 * Troca o estado (e a junta) ou o município do processo depois de criado (19/10, Camila: "algumas empresas
 * gostariam de mudar de estado"). Mesmas regras do modal de criação: a junta sai da UF, e em SP/RJ/BA o município é
 * obrigatório porque decide capital × interior.
 */
export const TrocarLocalModal: React.FC<TrocarLocalModalProps> = ({
  isOpen,
  onClose,
  ufAtual,
  municipioAtual,
  isLoading = false,
  onConfirm,
}) => {
  const [uf, setUf] = useState('GO');
  const [municipio, setMunicipio] = useState('');

  // Recarrega do processo a cada abertura, como o de renomear.
  useEffect(() => {
    if (!isOpen) return;
    const atual = (ufAtual ?? '').trim().toUpperCase();
    setUf(JUNTA_POR_UF[atual] && UFS_ATENDIDAS.includes(atual) ? atual : 'GO');
    setMunicipio(municipioAtual ?? '');
  }, [isOpen, ufAtual, municipioAtual]);

  const sigla = JUNTA_POR_UF[uf] ?? '';
  const limpo = municipio.trim();
  const mudou =
    uf !== (ufAtual ?? '').trim().toUpperCase() || limpo !== (municipioAtual ?? '').trim();
  const municipioOk = !ufExigeMunicipio(uf) || !!limpo;
  const generico = resolverRegistry(uf, limpo)?.generico;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader display="flex" alignItems="center" gap={2} fontSize="18px" fontWeight="700">
          <MapPin size={18} weight="duotone" />
          Trocar estado ou município
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <FormControl>
            <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
              UF da junta
            </FormLabel>
            <Flex align="center" gap={3}>
              <Select
                value={uf}
                onChange={e => setUf(e.target.value)}
                borderRadius="lg"
                maxW="120px"
                aria-label="UF da junta"
              >
                {UFS_ATENDIDAS.map(u => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </Select>
              <Text fontSize="13px" fontWeight="700" color="gray.700">
                Junta {sigla || '—'}
              </Text>
            </Flex>
            {generico && (
              <Text fontSize="11px" color="#B4750B" mt={2}>
                Fluxo <b>genérico</b> para {uf}: o roteiro segue o padrão nacional (REDESIM), mas as
                exigências desta junta não foram levantadas.
              </Text>
            )}
          </FormControl>

          <FormControl mt={4}>
            <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
              Município (cidade da sede)
              {ufExigeMunicipio(uf) && (
                <Text as="span" color="#C22B2B" ml={1}>
                  *
                </Text>
              )}
            </FormLabel>
            <Input
              value={municipio}
              onChange={e => setMunicipio(e.target.value)}
              placeholder="ex.: Goiânia, São Paulo, Rio de Janeiro"
              borderRadius="lg"
              aria-label="Município"
            />
            {ufExigeMunicipio(uf) && (
              <Text fontSize="11px" color="#B4750B" mt={1}>
                Obrigatório em {uf}: a junta trata capital e interior de formas diferentes.
              </Text>
            )}
          </FormControl>

          {/* O que muda: o card segue o registry da UF nova daqui pra frente. As etapas já feitas ficam como
              estão (nada é apagado), mas a viabilidade e os órgãos passam a pedir os itens do outro estado. */}
          {mudou && (
            <Flex
              align="flex-start"
              gap={2}
              mt={4}
              px={3}
              py={2.5}
              bg="orange.50"
              borderWidth="1px"
              borderColor="orange.200"
              borderRadius="lg"
            >
              <Box mt="1px" flexShrink={0}>
                <Warning size={15} weight="fill" color="var(--chakra-colors-orange-500)" />
              </Box>
              <Text fontSize="11px" color="orange.800" lineHeight="1.5">
                Daqui pra frente o card segue o novo local: os passos da viabilidade, os órgãos e os
                nomes de protocolo e guia passam a ser os dele. O que já foi feito nas etapas
                anteriores fica como está.
              </Text>
            </Flex>
          )}
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" borderRadius="full" onClick={onClose} isDisabled={isLoading}>
            Cancelar
          </Button>
          <Button
            colorScheme="purple"
            borderRadius="full"
            px={5}
            onClick={() =>
              onConfirm({ junta_uf: uf, junta_sigla: sigla, municipio: limpo || null })
            }
            isLoading={isLoading}
            isDisabled={!mudou || !municipioOk}
          >
            Salvar local
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TrocarLocalModal;
